"use client"
import { type FragmentOf, readFragment } from "@opensea/graphql"
import {
  Combobox,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxItem,
  ComboboxItemContent,
  ComboboxItemTitle,
  ComboboxList,
  classNames,
  FlexCenter,
  Pill,
  TextBody,
  Tooltip,
} from "@opensea/ui-kit"
import { Add, Info } from "@opensea/ui-kit/icons"
import { useEffect, useMemo, useState } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslations } from "use-intl"
import { useSnapshot } from "valtio"
import { Button } from "../../../../../components/Button"
import { useFlag } from "../../../../../providers/FlagProvider/useFlag"
import { FlowActions, FlowState } from "../../../../../stores/flowStore"
import type { CreateCollectionOfferFlowState } from "../../../../flow-state"
import { MAX_NUM_TRAITS_FOR_MULTI_TRAIT_OFFERS } from "../../../constants"
import type { CreateCollectionOfferFormValues } from "../../types"
import { CreateCollectionOfferTraitInputFragment } from "./CreateCollectionOfferTraitInput.fragment"

type CreateCollectionOfferTraitInputProps = {
  className?: string
  collection: FragmentOf<typeof CreateCollectionOfferTraitInputFragment>
  traitEnabled: boolean
}

export const CreateCollectionOfferTraitInput = ({
  className,
  collection: collectionKey,
  traitEnabled,
}: CreateCollectionOfferTraitInputProps) => {
  const t = useTranslations("wallet.CreateCollectionOfferTraitInput")
  const { setValue, getValues, control } =
    useFormContext<CreateCollectionOfferFormValues>()
  const collection = readFragment(
    CreateCollectionOfferTraitInputFragment,
    collectionKey,
  )
  const [isAdding, setIsAdding] = useState(false)
  const attributes = collection.attributes.items
  const { flow } = useSnapshot(FlowState)
  // Feature flag to enable multi-trait UX in the form
  const multiTraitOffersEnabled = useFlag("multiTraitOffersEnabled")

  const [forceMultiTraitMode, setForceMultiTraitMode] = useState(false)
  const isMultiTraitMode =
    forceMultiTraitMode ||
    (flow?.type === "create-collection-offer" &&
      flow.traits !== undefined &&
      flow.traits.length > 1)

  const flattenedAttributes = useMemo(() => {
    return attributes
      .filter(({ isNumericRange }) => !isNumericRange)
      .flatMap(({ traitType, values }) =>
        values.map(({ value }) => ({
          traitType,
          value,
        })),
      )
  }, [attributes])

  const trait = useWatch({ control, name: "offers.0.trait" })
  const watchedTraits = useWatch({ control, name: "offers.0.traits" })

  const traits = useMemo(() => watchedTraits || [], [watchedTraits])

  useEffect(() => {
    FlowActions.setFlowField<CreateCollectionOfferFlowState>("trait", trait)
  }, [trait])

  useEffect(() => {
    FlowActions.setFlowField<CreateCollectionOfferFlowState>("traits", traits)
  }, [traits])

  const content = useMemo(() => {
    const searchBox = (
      <div className="w-[288px]">
        <Combobox
          clearable
          content={
            <ComboboxList>
              <ComboboxGroup>
                <ComboboxEmpty>{t("noResults")}</ComboboxEmpty>
                {flattenedAttributes.map(({ traitType, value }) => {
                  return (
                    <ComboboxItem
                      key={`${traitType} ${value}`}
                      onSelect={() => {
                        if (isMultiTraitMode) {
                          const currentTraits =
                            getValues("offers.0.traits") || []
                          const newTrait = { traitType, value }

                          const traitExists = currentTraits.some(
                            t => t.traitType === traitType && t.value === value,
                          )

                          if (
                            !traitExists &&
                            currentTraits.length <
                              MAX_NUM_TRAITS_FOR_MULTI_TRAIT_OFFERS
                          ) {
                            setValue("offers.0.traits", [
                              ...currentTraits,
                              newTrait,
                            ])
                          }
                        } else {
                          setValue("offers.0.trait", { traitType, value })
                        }
                        setIsAdding(false)
                      }}
                      value={`${traitType} ${value}`}
                    >
                      <ComboboxItemContent>
                        <div className="w-full truncate">
                          <TextBody
                            className="mr-2"
                            color="text-secondary"
                            size="sm"
                          >
                            {traitType}
                          </TextBody>
                          <ComboboxItemTitle>{value}</ComboboxItemTitle>
                        </div>
                      </ComboboxItemContent>
                    </ComboboxItem>
                  )
                })}
              </ComboboxGroup>
            </ComboboxList>
          }
          overrides={{ Popover: { portal: false } }}
          placeholder={t("searchByTrait")}
          size="sm"
        />
      </div>
    )

    if (isMultiTraitMode) {
      if (traits.length > 0) {
        return (
          <FlexCenter className="flex-wrap gap-2">
            {traits.map((trait, index) => (
              <Pill
                key={`${trait.traitType}-${trait.value}`}
                onClick={() => {
                  const updatedTraits = traits.filter((_, i) => i !== index)
                  setValue("offers.0.traits", updatedTraits)
                }}
              >
                <TextBody
                  className="mr-1"
                  color="text-secondary"
                  size="xs"
                  weight="regular"
                >
                  {trait.traitType}
                </TextBody>
                {trait.value}
              </Pill>
            ))}
            {traitEnabled &&
              traits.length < MAX_NUM_TRAITS_FOR_MULTI_TRAIT_OFFERS &&
              (isAdding ? (
                searchBox
              ) : (
                <Button
                  icon={<Add />}
                  onClick={() => setIsAdding(true)}
                  size="sm"
                  variant="secondary"
                >
                  {t("add")}
                </Button>
              ))}
          </FlexCenter>
        )
      }

      return (
        <FlexCenter className="gap-4">
          {!traitEnabled && (
            <Tooltip
              content={
                <TextBody>{t("traitOffersNotSupportedTooltip")}</TextBody>
              }
              side="bottom"
            >
              <FlexCenter className="gap-1">
                <TextBody>{t("traitOffersNotSupported")}</TextBody>
                <Info size={16} />
              </FlexCenter>
            </Tooltip>
          )}
          {isAdding ? (
            searchBox
          ) : (
            <Button
              disabled={!traitEnabled}
              icon={<Add />}
              onClick={() => setIsAdding(true)}
              size="sm"
              variant="secondary"
            >
              {t("add")}
            </Button>
          )}
        </FlexCenter>
      )
    }

    if (isAdding) {
      return searchBox
    }

    if (trait) {
      return (
        <FlexCenter className="gap-2">
          <Pill
            onClick={() => {
              setValue("offers.0.trait", undefined)
              setIsAdding(true)
            }}
          >
            <TextBody
              className="mr-1"
              color="text-secondary"
              size="xs"
              weight="regular"
            >
              {trait.traitType}
            </TextBody>
            {trait.value}
          </Pill>
          {multiTraitOffersEnabled && traitEnabled && (
            <Button
              icon={<Add />}
              onClick={() => {
                // Convert single-trait selection into multi-trait mode
                setValue("offers.0.traits", [
                  { traitType: trait.traitType, value: trait.value },
                ])
                setValue("offers.0.trait", undefined)
                setForceMultiTraitMode(true)
                setIsAdding(true)
              }}
              size="sm"
              variant="secondary"
            >
              {t("add")}
            </Button>
          )}
        </FlexCenter>
      )
    }

    return (
      <FlexCenter className="gap-4">
        {!traitEnabled && (
          <Tooltip
            content={<TextBody>{t("traitOffersNotSupportedTooltip")}</TextBody>}
            side="bottom"
          >
            <FlexCenter className="gap-1">
              <TextBody>{t("traitOffersNotSupported")}</TextBody>
              <Info size={16} />
            </FlexCenter>
          </Tooltip>
        )}
        <Button
          disabled={!traitEnabled}
          icon={<Add />}
          onClick={() => setIsAdding(true)}
          size="sm"
          variant="secondary"
        >
          {t("add")}
        </Button>
      </FlexCenter>
    )
  }, [
    flattenedAttributes,
    isAdding,
    isMultiTraitMode,
    setValue,
    trait,
    traits,
    traitEnabled,
    multiTraitOffersEnabled,
    t,
    getValues,
  ])

  return (
    <FlexCenter
      className={classNames("w-full justify-between gap-2 py-2", className)}
    >
      <TextBody weight="semibold">{t("trait")}</TextBody>
      {content}
    </FlexCenter>
  )
}
